import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';

export interface Option {
  value: string;
  label: string;
}

@Component({
  selector: 'app-single-select-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: './single-select-dropdown.component.html',
  styleUrls: ['./single-select-dropdown.component.css'],
})
export class SingleSelectDropdownComponent {
  @Input() options: Option[] = [];
  @Input() placeholder: string = 'Select an option';
  @Input() selectedValue: string | null | boolean = null;
  @Output() selectedValueChange = new EventEmitter<string | null>();
  @Output() addMore = new EventEmitter<void>();
  @Input() disabled: boolean = false;
  @Input() allowAddNew: boolean = false;
  @Input() addNewLabel: string = 'Add new';
  @Input() searchable: boolean = false;
  dropdownOpen = false;
  searchQuery = '';
  // Panel width, captured once when the dropdown opens (see onToggle). Must NOT
  // be a getter that reads offsetWidth: measuring layout during change detection
  // makes the value oscillate with the page scrollbar (e.g. 348<->333), which
  // throws NG0100 and drives an infinite reposition->reflow->CD loop (frozen UI).
  menuWidth = 0;

  // The panel renders in a body-level CDK overlay (not inside the fixed
  // mobile-page-shell), so iOS no longer steals row taps and the panel scrolls
  // natively. Prefer opening downward; fall back to upward when there isn't
  // room below — this replaces the old manual drop-up / max-height logic.
  readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 2,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -2,
    },
  ];

  constructor(private elementRef: ElementRef) {}

  // Helper method to convert selectedValue to string for consistent handling
  private getSelectedValueAsString(): string | null {
    if (typeof this.selectedValue === 'boolean') {
      return this.selectedValue ? 'true' : 'false';
    }
    return this.selectedValue;
  }

  // Improved getSelectedLabel method
  getSelectedLabel(): string {
    const stringValue = this.getSelectedValueAsString();

    if (!stringValue || !this.options || this.options.length === 0) {
      return '';
    }

    const selectedOption = this.options.find(
      (opt) => opt.value === stringValue
    );
    return selectedOption ? selectedOption.label : '';
  }

  // Add a method to check if we should show placeholder
  shouldShowPlaceholder(): boolean {
    const stringValue = this.getSelectedValueAsString();
    return !stringValue || stringValue === '' || !this.getSelectedLabel();
  }

  // Add a method to check if we should show selected value
  shouldShowSelectedValue(): boolean {
    const stringValue = this.getSelectedValueAsString();
    return !!(
      stringValue &&
      stringValue !== '' &&
      this.getSelectedLabel() !== ''
    );
  }

  // Stable identity for *ngFor so option DOM nodes are reused when the options
  // array reference changes but values are unchanged. Without this, callers that
  // bind [options] to a method/getter returning a new array each change-detection
  // cycle (e.g. manufacturing Orders/BOM) cause every option node to be torn down
  // and rebuilt on each CD while the panel is open — on iOS this locks the main
  // thread/compositor and the dropdown "freezes".
  trackByValue(_i: number, opt: Option): string {
    return opt.value;
  }

  get filteredOptions(): Option[] {
    if (!this.searchable || !this.searchQuery) return this.options;
    const q = this.searchQuery.toLowerCase();
    return this.options.filter((o) => o.label.toLowerCase().includes(q));
  }

  onToggle() {
    if (this.disabled) return;
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      // Capture the toggle width once, before the overlay/backdrop can affect
      // the page scrollbar. Bound to [cdkConnectedOverlayWidth] as a stable
      // value so change detection never re-measures layout.
      const toggle = this.elementRef.nativeElement.querySelector(
        '.dropdown-toggle-borders'
      ) as HTMLElement | null;
      this.menuWidth = toggle ? toggle.offsetWidth : 0;
    } else {
      this.searchQuery = '';
    }
  }

  closeDropdown() {
    this.dropdownOpen = false;
    this.searchQuery = '';
  }

  selectOption(option: Option) {
    this.selectedValue = option.value;
    this.selectedValueChange.emit(option.value);
    this.dropdownOpen = false;
    this.searchQuery = '';
  }

  onAddMore(event: Event) {
    event.stopPropagation();
    this.addMore.emit();
    this.dropdownOpen = false;
  }

  onAddMoreIndustry() {
    this.dropdownOpen = false;
  }
}
